import React, { useEffect, useRef, useState, useContext, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import clsx from 'clsx';
import SVG from 'react-inlinesvg';
import styles from './UserDropdown.module.scss';
import { useAppSelector, useAppDispatch } from '@/store';
import { authSelector } from '@/store/reducers/auth';
import { authActions } from '@/store/reducers/auth';
import userThunks from '@/store/thunks/user';
import { useAdminTranslation } from '@/admin/hooks/useAdminTranslation';
import { useTranslation } from 'react-i18next';
import { useSchoolDetails } from '@/school-management/hooks/useSchoolDetails';
import { useGuidesItems } from '@/components/GuidesSection/GuidesSection';
import { UserRole } from '@/school-management/types/roles';
import dropdownLogout from '@/assets/svg/dropdown-logout.svg';
import dropdownSettings from '@/assets/svg/dropdown-settings.svg';
import dropdownContact from '@/assets/svg/dropdown-contact.svg';
import dropdownGuides from '@/assets/svg/dropdown-guides.svg';
import dropdownPrivacyPolicy from '@/assets/svg/privacyPolicy.svg';
import { EventDispatcherContext, DispatcherEvents } from '@/context/eventDispatcherContext';
import teacherDashboardThunks from '@/store/thunks/teacher-dashboard';
import userUtils from '@/utils/user';
import UseLevel from '@/hooks/UseLevel';
import AppIcon from '@/components/school-management/AppIcon/AppIcon';

interface UserDropdownProps {
  isOpen: boolean;
  isStudent?: boolean;
  onOpenSettings: () => void;
  onContactUs: () => void;
  onClose: () => void;
  anchorRef: React.RefObject<HTMLElement>;
}

const UserDropdown: React.FC<UserDropdownProps> = ({
  isOpen,
  isStudent = false,
  onOpenSettings,
  onContactUs,
  onClose,
  anchorRef,
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useAdminTranslation();
  const { t: tApp } = useTranslation();
  const { user, userRole } = useAppSelector(authSelector);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [isGuidesOpen, setIsGuidesOpen] = useState(false);
  const { getLevelText } = UseLevel();

  const eventDispatcher = useContext(EventDispatcherContext);

  const organizationId = user?.metadata?.organizationDetails?.organizationId;
  const isSchoolAdmin = userRole === UserRole.Admin;
  const isInGames = location.pathname.includes('/games') || location.pathname.includes('/quiz');

  // Only admins fetch school details — teachers derive all data from user metadata
  const { data: schoolData, refetchInBackground: refetchSchoolDetails } = useSchoolDetails(
    isSchoolAdmin ? organizationId : undefined
  );

  const school = schoolData?.data?.data?.school;
  const guidesItems = useGuidesItems(school?.classCodeEnabled);
  const numberOfClasses =
    school?.numberOfClasses ??
    user?.metadata?.organizationDetails?.teacher?.teacherGroups?.length ??
    0;
  const numberOfStudents =
    school?.numberOfStudents ?? user?.metadata?.organizationDetails?.teacher?.totalStudents ?? 0;
  const numberOfTeachers = school?.numberOfTeachers ?? 0;

  const firstName = user?.metadata?.firstName ?? '';
  const lastName = user?.metadata?.lastName ?? '';
  const fullName = `${firstName} ${lastName}`.trim();

  const schoolName = school?.name ?? user?.metadata?.organizationDetails?.organizationName ?? '';
  const schoolCity = school?.city ?? '';
  const schoolLocation = [schoolName, schoolCity].filter(Boolean).join(', ');

  const levelText = isStudent ? getLevelText(user?.progress?.userLevel) : '';
  const grade = user?.metadata?.organizationDetails?.grade;
  const gradeNum = grade ? parseInt(grade, 10) : null;
  const gradeText =
    isStudent && gradeNum && gradeNum >= 1 && gradeNum <= 13 ? tApp(`grade_${gradeNum}`) : '';

  const teacherLevel =
    !isStudent && !isSchoolAdmin && isInGames
      ? parseInt(localStorage.getItem('selectedLevel') || '1', 10)
      : null;

  const stats = isStudent
    ? [
        ...(levelText ? [{ label: tApp('level'), value: levelText }] : []),
        ...(gradeText ? [{ label: tApp('grade'), value: gradeText }] : []),
      ]
    : [
        ...(isSchoolAdmin ? [{ label: t('common.tabs.teachers'), value: numberOfTeachers }] : []),
        { label: t('common.tabs.classes'), value: numberOfClasses },
        { label: t('common.tabs.students'), value: numberOfStudents },
        ...(teacherLevel ? [{ label: tApp('level'), value: teacherLevel }] : []),
      ];

  const syncSchoolDetails = useCallback(() => {
    if (userUtils.isAdmin(user)) {
      refetchSchoolDetails();
    } else if (userUtils.isTeacher(user)) {
      dispatch(teacherDashboardThunks.fetchTeacherGroups());
    }
  }, [user, refetchSchoolDetails, dispatch]);

  useEffect(() => {
    eventDispatcher.addListener(DispatcherEvents.SCHOOL_DETAILS_CHANGE, syncSchoolDetails);

    return () => {
      eventDispatcher.removeListener(DispatcherEvents.SCHOOL_DETAILS_CHANGE, syncSchoolDetails);
    };
  }, [syncSchoolDetails, eventDispatcher]);

  useEffect(() => {
    if (!isSchoolAdmin) return;
    const onWindowFocus = () => refetchSchoolDetails();
    window.addEventListener('focus', onWindowFocus);
    return () => window.removeEventListener('focus', onWindowFocus);
  }, [isSchoolAdmin, refetchSchoolDetails]);

  useEffect(() => {
    if (!isOpen || !isSchoolAdmin) return;
    refetchSchoolDetails();
  }, [isOpen, isSchoolAdmin, refetchSchoolDetails]);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(target) &&
        anchorRef.current &&
        !anchorRef.current.contains(target)
      ) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose, anchorRef]);

  const handleSettings = () => {
    onOpenSettings();
    onClose();
  };

  const handleContactUs = () => {
    onContactUs();
    onClose();
  };

  const handlePrivacyPolicy = () => {
    const newWindow = window.open(
      'https://corporate.britannica.com/privacy.html',
      '_blank',
      'noopener,noreferrer'
    );
    if (newWindow) newWindow.opener = null;
    onClose();
  };

  const handleLogout = async () => {
    onClose();
    try {
      await dispatch(userThunks.signOut());
    } catch {}
    dispatch(authActions.signOut());
    navigate('/login');
  };

  if (!isOpen) return null;

  return (
    <div ref={dropdownRef} className={styles.dropdown} role='menu'>
      <div className={styles.userSection}>
        <span className={styles.userName}>{fullName}</span>
        {schoolLocation && <span className={styles.schoolLine}>{schoolLocation}</span>}
        {stats.length > 0 && (
          <div className={styles.statsLine}>
            {stats.map((stat, index) => (
              <React.Fragment key={stat.label}>
                {index > 0 && <span className={styles.statSeparator}>|</span>}
                <span className={styles.statItem}>
                  <span className={styles.statLabel}>{stat.label}:</span>
                  <strong>{stat.value}</strong>
                </span>
              </React.Fragment>
            ))}
          </div>
        )}
      </div>

      <div className={styles.divider} />

      <div className={styles.menuSection}>
        <button className={styles.menuItem} onClick={handleSettings} role='menuitem'>
          <SVG src={dropdownSettings} className={styles.menuIcon} />
          <span>{t('settings.title')}</span>
        </button>

        {!isStudent && guidesItems.length > 0 && (
          <>
            <button
              className={styles.menuItem}
              onClick={() => setIsGuidesOpen(prev => !prev)}
              role='menuitem'
              aria-expanded={isGuidesOpen}
            >
              <SVG src={dropdownGuides} className={styles.menuIcon} />
              <span className={styles.guidesLabel}>{t('guides')}</span>
              <svg
                className={clsx(styles.chevron, isGuidesOpen && styles.chevronOpen)}
                viewBox='0 0 24 24'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
              >
                <path
                  d='M6 9L12 15L18 9'
                  stroke='currentColor'
                  strokeWidth='1.5'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
            </button>

            {isGuidesOpen && (
              <div className={styles.guidesSubmenu}>
                {guidesItems.map((item, index) => (
                  <button
                    key={index}
                    className={clsx(styles.menuItem, styles.submenuItem)}
                    onClick={() => {
                      if (item.onTap) item.onTap();
                      onClose();
                    }}
                    role='menuitem'
                  >
                    <span className={styles.submenuIndex}>{index + 1}.</span>
                    <span className={styles.submenuItemTitle}>{item.title}</span>
                    {item.customArrowIcon && (
                      <SVG
                        src={item.customArrowIcon}
                        className={styles.submenuDownloadIcon}
                        width={20}
                        height={20}
                      />
                    )}
                  </button>
                ))}
              </div>
            )}
          </>
        )}

        {!isStudent && (
          <button className={styles.menuItem} onClick={handleContactUs} role='menuitem'>
            <SVG src={dropdownContact} className={styles.menuIcon} />
            <span>{t('contactUs')}</span>
          </button>
        )}

        <button
          className={styles.menuItem}
          onClick={handlePrivacyPolicy}
          role='menuitem'
          aria-label={`${t('settings.privacy_policy')}`}
        >
          <SVG src={dropdownPrivacyPolicy} className={styles.menuIcon} />
          <span className={styles.privacyPolicyLabel}>{t('settings.privacy_policy')}</span>
          <AppIcon icon='blankLink' className={styles.blankLink} />
        </button>

        <button className={styles.menuItem} onClick={handleLogout} role='menuitem'>
          <SVG src={dropdownLogout} className={styles.menuIcon} />
          <span>{t('settings.logout')}</span>
        </button>
      </div>
    </div>
  );
};

export default UserDropdown;
